import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { DataGridPremium, GridToolbar } from "@mui/x-data-grid-premium";
import { useParams } from "react-router-dom";
import { getAggregatedData, getCompanySummary } from "./api/underwritingApi";
import getTenantIdFromURL from "./utils/getTenantIdFromURL";

// Helper function to format numbers as currency
const formatNumber = (number) => {
  if (!number || isNaN(parseFloat(number))) return "";
  const num = parseFloat(number);
  if (num >= 1e9) {
    return `US $${(num / 1e9).toFixed(2)} billion`;
  } else if (num >= 1e6) {
    return `US $${(num / 1e6).toFixed(2)} million`;
  } else if (num >= 1e3) {
    return `US $${(num / 1e3).toFixed(2)} thousand`;
  } else {
    return `US $${num}`;
  }
};

const columns = [
  {
    field: "rowName",
    headerName: "",
    cellClassName: "first-column",
    sortable: false,
  },
  { field: "headquarters", headerName: "Location" },
  { field: "employees", headerName: "Employees" },
  { field: "revenue", headerName: "Revenue" },
  { field: "netIncome", headerName: "Net Income" },
  { field: "totalEquity", headerName: "Total Equity" },
  { field: "marketableSecurities", headerName: "Marketable Securities" },
  { field: "currentLiabilities", headerName: "Current Liabilities" },
  { field: "marketCap", headerName: "Market Cap" },
  { field: "cashEquivalents", headerName: "Cash & Cash Equivalents" },
  { field: "totalAssets", headerName: "Total Assets" },
  { field: "funding", headerName: "Funding" },
  { field: "parentRevenue", headerName: "Parent Revenue" },
];

const sourceKeys = [
  "manual",
  "alpha_vantage",
  "fmp",
  "financialStatements",
  "wikipedia",
  "dnb",
  "zoominfo",
  "linkedin",
  "gemini",
  "yahoo"
];

const moneyFields = [
  "revenue",
  "netIncome",
  "totalEquity",
  "marketableSecurities",
  "currentLiabilities",
  "marketCap",
  "cashEquivalents",
  "totalAssets",
  "funding",
  "parentRevenue"
];

export default function CompanyAnalysisTable() {
  const { companyId } = useParams();
  const tenantId = getTenantIdFromURL();

  const [rows, setRows] = useState([]);
      
  useEffect(() => {
    async function fetchData() {
    try {
        const aggData = await getAggregatedData(companyId);
        const companySummary = await getCompanySummary(tenantId, companyId);
        const standardizedData = aggData?.standardizedData;
        const resolvedDataForAggregation = aggData?.resolvedData;
        const { resolvedData = {} } = companySummary || {};


        // Build rows for each source
        const dataRows = sourceKeys.map((source, idx) => {
          const row = { id: idx + 1, rowName: source };
          columns.forEach(col => {
            if (col.field !== "rowName") {
              let value;
              
              // For manual source, use resolvedData from companySummary
              if (source === "manual") {
                if(resolvedData[col.field]?.source === "manualSource") {
                  value = resolvedData[col.field]?.value ?? "";
                } else {
                  // Check both direct field access and .value property for monetary fields
                  const fieldData = standardizedData[source]?.[col.field];
                  value = (fieldData?.value !== undefined) ? fieldData.value : (fieldData ?? "");
                }
              } else {
                // Check both direct field access and .value property for monetary fields
                const fieldData = standardizedData[source]?.[col.field];
                value = (fieldData?.value !== undefined) ? fieldData.value : (fieldData ?? "");
              }
              
              if (moneyFields.includes(col.field)) {
                value = formatNumber(value);
              }
              row[col.field] = value;
            }
          });
          return row;
        });

        // UW Source row (last row)
        const uwRow = { id: dataRows.length + 1, rowName: "UW Source" };
        columns.forEach(col => {
          if (col.field !== "rowName") {
            let value = resolvedDataForAggregation[col.field]?.source || "";
            if (moneyFields.includes(col.field)) {
              // Optionally, you could also format the value if you want to show the actual number instead of the source name
              // value = formatNumber(resolvedData[col.field]?.value);
            }
            uwRow[col.field] = value;
            }
        });

        setRows([...dataRows, uwRow]);
      } catch (error) {
        console.error("Error fetching analysis data:", error);
    }
    }
    if (companyId) fetchData();
  }, [companyId]);


  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Box sx={{ height: 600, width: "100%" }}>
        <DataGridPremium
          rows={rows}
          columns={columns}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true } }}
          pageSizeOptions={[5, 10, 50]}
          getRowClassName={(params) =>
            params.row.rowName === "UW Source" ? "uw-source-row" : ""
          }
          sx={{
            "& .first-column": {
              backgroundColor: "#f5f5f5",
              fontWeight: "bold",
              color: "#2F3D63",
            },
            "& .uw-source-row": {
              backgroundColor: "#e0f7fa",
              fontWeight: "bold",
              color: "#004d40",
            },
          }}
        />
      </Box>
    </Box>
  );
}
